import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components";
import { CALENDLY_URL } from "@/constants";
import { useTranslation } from "@/i18n";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

export const Hero: React.FC = () => {
  const t = useTranslation();

  const scrollTo = (elementId: string) => {
    document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative w-full bg-[#1d2128] overflow-hidden">
      {/* Circuit artwork, kept to the right and dimmed so the copy stays readable */}
      <div aria-hidden className="bg-rocknitive absolute inset-0 opacity-50" />
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-r from-[#1d2128] from-35% via-[#1d2128]/90 via-60% to-[#1d2128]/30"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[40%] bg-gradient-to-b from-[#1d2128] to-transparent"
      />

      <div className="relative max-w-screen-xl m-auto px-[38px] 2xl:px-0 pt-[64px] md:pt-[120px] pb-[40px] md:pb-[56px]">
        <motion.h1
          {...fadeUp}
          transition={{ duration: 0.6 }}
          className="text-white font-bold text-[40px] leading-[50px] md:text-[68px] md:leading-[80px] max-w-4xl"
        >
          {t.hero.headlinePlain}{" "}
          <span className="text-[#01C6FB]">{t.hero.headlineAccent}</span>.
        </motion.h1>

        <motion.p
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-[20px] md:mt-[28px] max-w-2xl text-[19px] leading-[30px] md:text-[24px] md:leading-[38px] font-medium text-white"
        >
          {t.hero.sub}
        </motion.p>

        <motion.p
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-[16px] max-w-2xl text-[15px] leading-[25px] md:text-[17px] md:leading-[28px] text-base-300"
        >
          {t.hero.tagline}
        </motion.p>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-[32px] md:mt-[44px] flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer">
            <Button
              size="large"
              shape="circle"
              className="bg-[#01C6FB] text-[#10141a] font-semibold border-0 px-8 hover:bg-[#4dd7fc]"
            >
              {t.hero.ctaPrimary}
            </Button>
          </a>
          <Button
            size="large"
            shape="circle"
            variant="ghost"
            className="border border-white/40 bg-[#1d2128]/60 px-8 hover:border-[#01C6FB] hover:text-[#01C6FB]"
            onClick={() => scrollTo("prozess")}
          >
            {t.hero.ctaSecondary}
          </Button>
        </motion.div>
      </div>

      {/* Trust bar */}
      <div className="relative border-t border-white/10 bg-[#171a20]/85">
        <div className="max-w-screen-xl m-auto px-[38px] 2xl:px-0 py-[24px] md:py-[28px] flex flex-wrap justify-between gap-x-8 gap-y-3">
          {t.hero.trust.map((item, index) => (
            <motion.div
              key={`trust-${index}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.45 + index * 0.1 }}
              className="flex items-center whitespace-nowrap text-[14px] md:text-[15px] text-base-200"
            >
              <span className="mr-3 h-[6px] w-[6px] shrink-0 rounded-full bg-[#01C6FB]" />
              {item}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};
